import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { supabase } from '../../lib/supabaseClient'

// Async thunks for fetching products
export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (_, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false })
      
      if (error) throw error
      
      return data || []
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const fetchProductsByLeague = createAsyncThunk(
  'products/fetchProductsByLeague',
  async (league, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('league', league)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// Async thunk for fetching a single product by ID
export const fetchProductById = createAsyncThunk(
  'products/fetchProductById',
  async (id, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single()
      
      if (error) throw error
      
      if (!data) {
        throw new Error('Product not found')
      }
      
      // Process the product data to handle images correctly
      let processedProduct = { ...data }
      
      // Process additional_images if needed
      if (data.additional_images && typeof data.additional_images === 'string') {
        try {
          processedProduct.additional_images = JSON.parse(data.additional_images)
        } catch (err) {
          console.error('Error parsing additional_images:', err)
          processedProduct.additional_images = []
        }
      }
      
      return processedProduct
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

const initialState = {
  products: [],
  featuredProducts: [],
  currentProduct: null,
  loading: false,
  error: null
}

export const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    setProducts: (state, action) => {
      state.products = action.payload;
    },
    setFeaturedProducts: (state, action) => {
      state.featuredProducts = action.payload;
    },
    setCurrentProduct: (state, action) => {
      state.currentProduct = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearProducts: (state) => {
      state.products = [];
      state.featuredProducts = [];
      state.currentProduct = null;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchProducts
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.products = action.payload
        state.loading = false
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Handle fetchProductsByLeague
      .addCase(fetchProductsByLeague.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProductsByLeague.fulfilled, (state, action) => {
        state.loading = false
        state.products = action.payload
      })
      .addCase(fetchProductsByLeague.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Handle fetchProductById
      .addCase(fetchProductById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        // Add product to state if it doesn't already exist
        const exists = state.products.some(p => p.id === action.payload.id)
        if (!exists) {
          state.products.push(action.payload)
        } else {
          // Update existing product
          state.products = state.products.map(p => 
            p.id === action.payload.id ? action.payload : p
          )
        }
        state.loading = false
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { 
  setProducts, 
  setFeaturedProducts, 
  setCurrentProduct, 
  setLoading, 
  setError, 
  clearProducts 
} = productsSlice.actions

export default productsSlice.reducer